"use client";

import React, { useEffect, useRef, useState } from "react";

import { useDebounce } from "@unreserved/hooks/use-debounce";
import type { PropertyAddressSuggestionsDto } from "@unreserved/sdk";
import { Search } from "lucide-react";

import { LocationIcon } from "@/app/_components/icons/get-estimates/location";
import { Input } from "@/app/_components/shared/input";
import { HERO_DATA } from "@/app/_libs/data/landing-page/hero-section";
import { usePropertyAddressSuggestions } from "@/hooks/use-listing";
import { cn } from "@/lib/utils";

interface AddressSearchInputProps {
  onPropertyChange?: (property: PropertyAddressSuggestionsDto | null) => void;
  onNoMatchingResultsChange?: (hasNoMatches: boolean) => void;
  onManualModeChange?: (enabled: boolean) => void;
  onChange?: (value: string) => void;
  value?: string;
  placeholder?: string;
  inputClassName?: string;
  initiallySelected?: boolean;
  manualMode?: boolean;
  acceptTypedValue?: boolean;
}

export function AddressSearchInput({
  onPropertyChange,
  onNoMatchingResultsChange,
  onChange,
  value,
  placeholder = HERO_DATA.searchPlaceholder,
  inputClassName = "",
  initiallySelected = false,
  manualMode = false,
  acceptTypedValue = false,
}: AddressSearchInputProps) {
  const [internalSearchQuery, setInternalSearchQuery] = useState(value ?? "");
  const searchQuery = value !== undefined ? value : internalSearchQuery;
  const [selectedProperty, setSelectedProperty] = useState<PropertyAddressSuggestionsDto | null>(
    initiallySelected && value ? ({ address: value } as PropertyAddressSuggestionsDto) : null,
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const { data: suggestionsData, isFetching } = usePropertyAddressSuggestions(
    debouncedSearchQuery,
    !selectedProperty && !manualMode,
  );

  const suggestions = suggestionsData?.data || [];
  const activeItemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSuggestions]);

  useEffect(() => {
    if (value !== undefined) {
      setInternalSearchQuery(value);
    }
  }, [value]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    if (value !== undefined) {
      onChange?.(query);
    } else {
      setInternalSearchQuery(query);
    }
    setActiveIndex(-1);

    if (selectedProperty) {
      setSelectedProperty(null);
    }
  };

  useEffect(() => {
    const query = debouncedSearchQuery.trim();

    if (!query || selectedProperty || manualMode) {
      setShowSuggestions(false);
      setActiveIndex(-1);
      return;
    }

    setShowSuggestions(true);
    setActiveIndex(suggestions.length > 0 ? 0 : -1);
  }, [debouncedSearchQuery, manualMode, selectedProperty, suggestions.length]);

  useEffect(() => {
    const query = debouncedSearchQuery.trim();
    const hasNoMatches =
      query.length > 0 && !selectedProperty && !isFetching && suggestions.length === 0;
    onNoMatchingResultsChange?.(hasNoMatches);
  }, [
    debouncedSearchQuery,
    isFetching,
    onNoMatchingResultsChange,
    selectedProperty,
    suggestions.length,
  ]);

  const handlePropertyClick = (property: PropertyAddressSuggestionsDto) => {
    setSelectedProperty(property);
    if (value !== undefined) {
      onChange?.(property.address);
    } else {
      setInternalSearchQuery(property.address);
    }
    setShowSuggestions(false);
    setActiveIndex(-1);
    onPropertyChange?.(property);
  };

  useEffect(() => {
    if (activeIndex >= 0 && activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeIndex]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "ArrowDown" && suggestions.length > 0) {
        setShowSuggestions(true);
        setActiveIndex(0);
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      return;
    }

    if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        handlePropertyClick(suggestions[activeIndex] as PropertyAddressSuggestionsDto);
      }
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="relative flex-1">
      <div className={cn("relative flex flex-1 items-center gap-2", inputClassName)}>
        {!manualMode && <Search className="text-border-600 m-0 size-5" />}
        <Input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (acceptTypedValue && !selectedProperty && !manualMode && searchQuery.trim()) {
              onPropertyChange?.({ address: searchQuery.trim() } as PropertyAddressSuggestionsDto);
            }
          }}
          className="text-typography-900 placeholder:text-typography-500 w-full border-none! p-0 text-xs shadow-none placeholder:text-sm focus:border-none! focus:shadow-none focus:outline-none! focus-visible:border-none! focus-visible:shadow-none focus-visible:ring-0! focus-visible:ring-offset-0! md:text-sm"
        />
      </div>

      {showSuggestions && (isFetching || suggestions.length > 0) && (
        <div
          className="border-border-100 bg-background-0 absolute z-50 mt-1 -ml-0.5! max-h-48 w-full overflow-y-auto rounded-md border p-1 shadow-lg"
          ref={dropdownRef}>
          {isFetching ? (
            <div className="text-typography-700 flex min-w-40 items-center px-3 py-2 text-sm leading-[21px]">
              Searching...
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((property, index) => (
              <SuggestionItem
                key={property.id}
                property={property}
                isActive={index === activeIndex}
                onClick={() => handlePropertyClick(property)}
                ref={index === activeIndex ? activeItemRef : null}
              />
            ))
          ) : null}
        </div>
      )}
    </div>
  );
}

const SuggestionItem = React.forwardRef<
  HTMLButtonElement,
  {
    property: PropertyAddressSuggestionsDto;
    onClick: () => void;
    isActive: boolean;
  }
>(({ property, onClick, isActive }, ref) => {
  return (
    <button
      ref={ref}
      key={property.id}
      type="button"
      onClick={onClick}
      className={cn(
        "hover:bg-background-50 z-9999 flex w-full cursor-pointer items-start gap-2 rounded p-3 text-left transition-colors",
        isActive && "bg-background-200",
      )}>
      <LocationIcon className="mt-0.5 size-5 shrink-0" fill="text-typography-900" />
      <div className="flex flex-col">
        <p className="text-typography-700 text-sm leading-5 font-semibold">{property.address}</p>
        {/* <p className="text-typography-700 text-sm leading-5 font-normal">
          {property.addressComponents.suburb}, {property.addressComponents.state}{" "}
          {property.addressComponents.postCode}
        </p> */}
      </div>
    </button>
  );
});

SuggestionItem.displayName = "SuggestionItem";
